import { Image } from '@tiptap/extension-image';

export const CustomImage = Image.extend({
  draggable: false,
  addAttributes() {
    return {
      ...this.parent?.(),
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute('alt') || null,
        renderHTML: (attributes) => {
          if (!attributes.alt) {
            return {};
          }
          return {
            alt: attributes.alt,
          };
        },
      },
      textAlign: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-text-align') || null,
        renderHTML: (attributes) => {
          if (!attributes.textAlign) {
            return {};
          }
          return {
            'data-text-align': attributes.textAlign,
            style: `text-align: ${attributes.textAlign};`,
          };
        },
      },
      width: {
        default: 'auto',
        parseHTML: (element) => element.getAttribute('width') || 'auto',
        renderHTML: (attributes) => {
          if (!attributes.width || attributes.width === 'auto') {
            return {};
          }
          return {
            width: attributes.width,
            style: `width: ${attributes.width};`,
          };
        },
      },
      height: {
        default: 'auto',
        parseHTML: (element) => element.getAttribute('height') || 'auto',
        renderHTML: (attributes) => {
          if (!attributes.height || attributes.height === 'auto') {
            return {};
          }
          return {
            height: attributes.height,
          };
        },
      },
    };
  },
  renderHTML({ node }) {
    const { src, alt, textAlign, width, height } = node.attrs;
    let style = '';
    if (width && width !== 'auto') {
      style += `width: ${width};`;
    }
    if (textAlign === 'center') {
      style += 'margin: 0 auto; display: block;';
    }
    if (textAlign === 'right') {
      style += 'margin-left: auto; margin-right: 0; display: block;';
    }
    return [
      'img',
      {
        src,
        alt: alt || undefined,
        width: width !== 'auto' ? width : undefined,
        height: height !== 'auto' ? height : undefined,
        'data-text-align': textAlign,
        style,
      },
    ];
  },

  addNodeView() {
    return ({ node, editor }) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'resizable-image-wrapper';
      wrapper.classList.add(`resizable-image-wrapper-${node.attrs.textAlign || 'left'}`);
      wrapper.addEventListener('dragstart', (e) => e.preventDefault());
      const imgContainer = document.createElement('div');
      imgContainer.className = 'image-container';
      if (node.attrs.width && node.attrs.width !== 'auto') {
        imgContainer.style.width = node.attrs.width;
      }
      if (node.attrs.height && node.attrs.height !== 'auto') {
        imgContainer.style.height = node.attrs.height;
      }

      const img = document.createElement('img');
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || '';
      img.draggable = false;

      if (node.attrs.width && node.attrs.width !== 'auto') {
        img.style.width = node.attrs.width;
      } else {
        img.style.width = '100%';
      }
      if (node.attrs.height && node.attrs.height !== 'auto') {
        img.style.height = node.attrs.height;
      } else {
        img.style.height = '100%';
      }

      imgContainer.appendChild(img);
      const handlePositions = [
        'top-left',
        'top-middle',
        'top-right',
        'middle-left',
        'middle-right',
        'bottom-left',
        'bottom-middle',
        'bottom-right',
      ];
      const resizerHandles: HTMLElement[] = [];
      const startResize = (event: PointerEvent, pos: string) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startY = event.clientY;
        const startWidth = imgContainer.offsetWidth;
        const startHeight = imgContainer.offsetHeight;
        const aspect = startWidth / startHeight;
        const onMove = (moveEvent: PointerEvent) => {
          moveEvent.preventDefault();
          const diffX = moveEvent.clientX - startX;
          const diffY = moveEvent.clientY - startY;
          let newWidth = startWidth;
          let newHeight = startHeight;
          if (pos.includes('left')) {
            newWidth = startWidth - diffX;
          }
          if (pos.includes('right')) {
            newWidth = startWidth + diffX;
          }
          if (pos.includes('top')) {
            newHeight = startHeight - diffY;
          }
          if (pos.includes('bottom')) {
            newHeight = startHeight + diffY;
          }
          if (!['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(pos)) {
            return;
          }
          newHeight = newWidth / aspect;
          if (newWidth < 10) {
            newWidth = 10;
          }
          if (newHeight < 10) {
            newHeight = 10;
          }
          imgContainer.style.width = `${newWidth}px`;
          imgContainer.style.height = `${newHeight}px`;
          img.style.width = `${newWidth}px`;
          img.style.height = `${newHeight}px`;
        };
        const onUp = (upEvent: PointerEvent) => {
          upEvent.preventDefault();
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          editor.view.dom.focus({ preventScroll: true });
          editor
            .chain()
            .updateAttributes('image', {
              width: imgContainer.style.width,
              height: imgContainer.style.height,
            })
            .run();
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
      };

      handlePositions.forEach((pos) => {
        const handle = document.createElement('div');
        handle.classList.add('resizer', pos);
        handle.style.display = 'none';
        handle.addEventListener('pointerdown', (e: PointerEvent) => {
          if (!['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(pos)) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          startResize(e, pos);
        });
        imgContainer.appendChild(handle);
        resizerHandles.push(handle);
      });

      wrapper.appendChild(imgContainer);

      return {
        dom: wrapper,
        selectNode() {
          resizerHandles.forEach((handle) => {
            handle.style.display = 'block';
          });
        },
        deselectNode() {
          resizerHandles.forEach((handle) => {
            handle.style.display = 'none';
          });
        },
        update(updatedNode) {
          if (updatedNode.attrs.width !== node.attrs.width) {
            imgContainer.style.width = updatedNode.attrs.width || 'auto';
            img.style.width = updatedNode.attrs.width || 'auto';
          }
          if (updatedNode.attrs.height !== node.attrs.height) {
            imgContainer.style.height = updatedNode.attrs.height || 'auto';
            img.style.height = updatedNode.attrs.height || 'auto';
          }
          if (updatedNode.attrs.src !== node.attrs.src) {
            img.src = updatedNode.attrs.src;
          }
          if (updatedNode.attrs.alt !== node.attrs.alt) {
            img.alt = updatedNode.attrs.alt || '';
          }
          if (updatedNode.attrs.textAlign !== node.attrs.textAlign) {
            wrapper.classList.remove(`resizable-image-wrapper-${node.attrs.textAlign || 'left'}`);
            wrapper.classList.add(
              `resizable-image-wrapper-${updatedNode.attrs.textAlign || 'left'}`,
            );
          }
          node = updatedNode;
          return true;
        },
      };
    };
  },
});

export default CustomImage;
